import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { createApiService, apiService } from "@/App";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import Header from "@/components/Header";
import { useLiveBotEvents } from "@/hooks/useLiveBotEvents";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import VirtualActivityFeed from "@/components/VirtualActivityFeed";

// Check for pending bot redirect on module load (handles mobile wallet return)
const checkPendingBotRedirect = () => {
  try {
    const pendingRedirect = localStorage.getItem('stxoshi_pending_bot_redirect');
    if (pendingRedirect) {
      const { botId, timestamp } = JSON.parse(pendingRedirect);
      // Only redirect if less than 5 minutes old
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return botId;
      }
      localStorage.removeItem('stxoshi_pending_bot_redirect');
    }
  } catch (e) {
    localStorage.removeItem('stxoshi_pending_bot_redirect');
  }
  return null;
};

import { 
  Bitcoin, 
  Bot, 
  Plus, 
  Activity, 
  TrendingUp, 
  Shield,
  RefreshCw,
  MoreVertical,
  Trash2,
  Eye,
  Play,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Cpu,
  Globe,
  CheckCircle,
  TrendingDown,
  Search,
  X
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";

const ADMIN_ADDRESSES = [
  'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN', // Treasury (VWN)
  'SP17175BTCCNHF06Z7TNP3PG9FET3JRFQ85C31WSY', // Admin 2
  'SP1WST899N8B2VR76DCHQ56ZQ78R9VSYT2B2RNTJY', // Admin 3
  'SP2R3H4VQEHPAV8VM8KS0JR8BAS8HTANZ92R64H8K', // Admin 4 (H8K)
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { isConnected, isLoading: walletLoading, user, addresses, getAuthHeaders } = useWallet();
  const { showUsd, stxoshiUsd, usdRate } = useCurrency();
  const _rDash = stxoshiUsd || 0.0000037;
  const _usdFmt = (u) => `$${u.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(u) < 0.01 ? 8 : 2 })}`;
  const fmtStxoshiWhole = (whole) => showUsd ? _usdFmt((Number(whole) || 0) * _rDash) : (Number(whole) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const fmtAssetWhole = (whole, asset) => {
    const rate = usdRate(asset);
    if (showUsd && rate) return _usdFmt((Number(whole) || 0) * rate);
    return (Number(whole) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };
  const dashUnit = showUsd ? "USD" : "STXOSHI";
  const authenticatedApi = createApiService(getAuthHeaders);
  
  // Check if connected wallet is an admin
  const isAdmin = isConnected && ADMIN_ADDRESSES.includes(addresses?.stx);
  
  // Initialize state from localStorage cache if available (prevents blank dashboard on mobile nav)
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(`stxoshi-dashboard-${key}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };
  
  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(`stxoshi-dashboard-${key}`, JSON.stringify(data));
    } catch {
      // Ignore storage errors
    }
  };

  const [agents, setAgents] = useState(() => getCachedData('agents') || []);
  const [predictionBots, setPredictionBots] = useState(() => getCachedData('predictionBots') || []);
  const [activities, setActivities] = useState(() => getCachedData('activities') || []);
  const [yields, setYields] = useState(() => getCachedData('yields') || []);
  const [pegStatus, setPegStatus] = useState(() => getCachedData('pegStatus'));
  const [stats, setStats] = useState(() => getCachedData('stats'));
  const [loading, setLoading] = useState(true);
  const [simulatingId, setSimulatingId] = useState(null);
  const [botSearch, setBotSearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");

  // Live trade events — flash bot rows & optimistically update P&L in ~1s
  const {
    connected: liveConnected,
    flashes: liveFlashes,
    tradeBumps: liveBumps,
    heartbeats: liveHeartbeats,
  } = useLiveBotEvents({
    onEvent: (ev) => {
      if (ev.type === "heartbeat") {
        // Tick fired — sync latest numbers, even when no trade happened
        setPredictionBots((curr) =>
          curr.map((b) =>
            b.id === ev.bot_id
              ? {
                  ...b,
                  total_pnl_pct:
                    typeof ev.pnl_pct === "number" ? ev.pnl_pct : b.total_pnl_pct,
                  total_trades:
                    typeof ev.total_trades === "number"
                      ? ev.total_trades
                      : b.total_trades,
                  total_balance_stxoshi:
                    typeof ev.total_balance_stxoshi === "number"
                      ? ev.total_balance_stxoshi
                      : b.total_balance_stxoshi,
                  tick_state: {
                    ...(b.tick_state || {}),
                    last_ticked_at: ev.ts || new Date().toISOString(),
                  },
                }
              : b
          )
        );
        return;
      }
      setPredictionBots((curr) =>
        curr.map((b) =>
          b.id === ev.bot_id
            ? {
                ...b,
                total_pnl_pct:
                  typeof ev.pnl_pct === "number" ? ev.pnl_pct : b.total_pnl_pct,
                total_trades: (b.total_trades || 0) + 1,
                last_trade_at: ev.ts || new Date().toISOString(),
              }
            : b
        )
      );
    },
  });

  // 1-second re-render tick so per-row countdown + progress bar refresh live
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => (n + 1) % 1000), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchData = async () => {
    // Don't fetch if wallet is still loading - wait for connection check to complete
    if (walletLoading) {
      console.log('[DASHBOARD] Skipping fetch - wallet still loading');
      return;
    }
    
    // Check if we're connected and have the STX address for authenticated requests
    const canFetchAuthenticated = isConnected && addresses?.stx;
    console.log('[DASHBOARD] Fetching data - isConnected:', isConnected, 'hasStxAddress:', !!addresses?.stx, 'canFetchAuthenticated:', canFetchAuthenticated);
    
    try {
      // Fetch all data in parallel
      const results = await Promise.allSettled([
        canFetchAuthenticated ? authenticatedApi.getAgents() : apiService.exploreAgents(),
        apiService.getRecentActivities(200),
        apiService.getYieldStrategies(),
        apiService.getPegStatus(),
        apiService.getStats(),
        // Always fetch all platform bots (for public leaderboard view).
        // NOTE: Do NOT swallow errors here with .catch() — if we did, an upstream
        // failure (e.g. CF 520) would resolve as "fulfilled" with empty bots and
        // wipe the cached bot list. Let it propagate as a rejected settlement so
        // the handler below preserves cached data on failure.
        apiService.get('/prediction-bots/public')
      ]);
      
      // Process results, logging any failures
      const [agentsResult, activitiesResult, yieldsResult, pegResult, statsResult, botsResult] = results;
      
      if (agentsResult.status === 'fulfilled') {
        const agentsData = agentsResult.value.data || [];
        
        // Set agents immediately with default positions (fast initial render)
        const initialAgents = agentsData.map(agent => ({
          ...agent,
          active_positions_count: agent.active_positions_count || 0,
          yield_earned: agent.total_yield_earned || 0,
          total_yield: agent.total_yield_earned || 0
        }));
        setAgents(initialAgents);
        setCachedData('agents', initialAgents);
        
        // Fetch positions in background (non-blocking) to update yield/positions
        setTimeout(async () => {
          try {
            const agentsWithPositions = await Promise.all(
              agentsData.slice(0, 10).map(async (agent) => { // Limit to first 10 agents
                try {
                  const controller = new AbortController();
                  const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout per agent
                  
                  const positionsRes = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.id}/defi/positions`,
                    { signal: controller.signal }
                  );
                  clearTimeout(timeoutId);
                  
                  if (positionsRes.ok) {
                    const posData = await positionsRes.json();
                    const yieldEarned = posData.positions?.reduce((total, pos) => {
                      return total + (pos.yield_earned || 0);
                    }, 0) || 0;
                    
                    return {
                      ...agent,
                      active_positions_count: posData.summary?.active_positions || 0,
                      yield_earned: yieldEarned,
                      total_yield: agent.total_yield_earned || yieldEarned
                    };
                  }
                } catch (err) {
                  // Silent fail - timeout or error
                }
                return { 
                  ...agent, 
                  active_positions_count: 0,
                  yield_earned: agent.total_yield_earned || 0,
                  total_yield: agent.total_yield_earned || 0
                };
              })
            );
            
            // Add remaining agents (if more than 10)
            const remainingAgents = agentsData.slice(10).map(agent => ({
              ...agent,
              active_positions_count: 0,
              yield_earned: agent.total_yield_earned || 0,
              total_yield: agent.total_yield_earned || 0
            }));
            
            const allAgents = [...agentsWithPositions, ...remainingAgents];
            
            // Sort agents by yield earned
            const sortedAgents = allAgents.sort((a, b) => {
              const yieldDiff = (b.total_yield || 0) - (a.total_yield || 0);
              if (yieldDiff !== 0) return yieldDiff;
              return (b.active_positions_count || 0) - (a.active_positions_count || 0);
            });
            
            setAgents(sortedAgents);
            setCachedData('agents', sortedAgents);
          } catch (e) {
            console.log('[DASHBOARD] Background positions fetch failed:', e);
          }
        }, 100); // Start background fetch after 100ms
        
      } else {
        console.error('[DASHBOARD] Agents fetch failed:', agentsResult.reason);
        // Don't clear agents - keep cached data
      }
      
      if (activitiesResult.status === 'fulfilled') {
        const activitiesData = activitiesResult.value.data || [];
        setActivities(activitiesData);
        setCachedData('activities', activitiesData);
      } else {
        console.error('[DASHBOARD] Activities fetch failed:', activitiesResult.reason);
        // Don't clear activities - keep cached data
      }
      
      if (yieldsResult.status === 'fulfilled') {
        const yieldsData = yieldsResult.value.data || [];
        setYields(yieldsData);
        setCachedData('yields', yieldsData);
      } else {
        console.error('[DASHBOARD] Yields fetch failed:', yieldsResult.reason);
        // Don't clear yields - keep cached data
      }
      
      if (pegResult.status === 'fulfilled') {
        setPegStatus(pegResult.value.data);
        setCachedData('pegStatus', pegResult.value.data);
      } else {
        console.error('[DASHBOARD] Peg status fetch failed:', pegResult.reason);
        // Don't clear pegStatus - keep cached data
      }
      
      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value.data);
        setCachedData('stats', statsResult.value.data);
        console.log('[DASHBOARD] Stats loaded successfully:', statsResult.value.data?.total_agents, 'agents');
      } else {
        console.error('[DASHBOARD] Stats fetch failed:', statsResult.reason);
        // Don't clear stats - keep cached data
      }
      
      if (botsResult.status === 'fulfilled') {
        const botsData = botsResult.value.data?.bots || [];
        // Only overwrite state/cache when the API actually returned bots.
        // An empty array from a momentarily-failing prod endpoint must NOT
        // wipe a previously-loaded list of platform bots.
        if (botsData.length > 0) {
          setPredictionBots(botsData);
          setCachedData('predictionBots', botsData);
          console.log('[DASHBOARD] Prediction bots loaded:', botsData.length);
        } else {
          console.log('[DASHBOARD] Prediction bots returned 0 — keeping cached list');
        }
      } else {
        console.error('[DASHBOARD] Prediction bots fetch failed:', botsResult.reason);
        // Don't clear predictionBots - keep cached data
      }
      
      console.log('[DASHBOARD] Fetch complete');
      
    } catch (err) {
      console.error("[DASHBOARD] Failed to fetch data:", err);
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for pending bot redirect (mobile wallet return scenario)
    const pendingBotId = checkPendingBotRedirect();
    if (pendingBotId) {
      console.log('[DASHBOARD] Found pending bot redirect:', pendingBotId);
      localStorage.removeItem('stxoshi_pending_bot_redirect');
      toast.success('Redirecting to your new bot...');
      navigate(`/bot/${pendingBotId}`, { replace: true });
      return;
    }

    console.log('[DASHBOARD] useEffect triggered - walletLoading:', walletLoading, 'isConnected:', isConnected, 'hasStxAddress:', !!addresses?.stx);
    
    // Only fetch when wallet loading is complete
    if (!walletLoading) {
      fetchData();
    }
    
    const interval = setInterval(() => {
      if (!walletLoading) {
        fetchData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, walletLoading, addresses?.stx]);

  const handleSimulateCycle = async (agentId) => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    setSimulatingId(agentId);
    try {
      const res = await authenticatedApi.simulateCycle(agentId);
      toast.success(`Cycle complete! ${res.data.activities_generated} activities generated`);
      fetchData();
    } catch (err) {
      toast.error("Simulation failed");
    } finally {
      setSimulatingId(null);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    try {
      await authenticatedApi.deleteAgent(agentId);
      toast.success("Agent deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete agent");
    }
  };

  // Mock chart data
  const chartData = [
    { time: "00:00", value: 0.0245 },
    { time: "04:00", value: 0.0248 },
    { time: "08:00", value: 0.0252 },
    { time: "12:00", value: 0.0249 },
    { time: "16:00", value: 0.0258 },
    { time: "20:00", value: 0.0261 },
    { time: "Now", value: 0.0265 },
  ];

  const COLORS = ['#FF9F43', '#00F0FF', '#9D4EDD', '#00E676'];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'transaction': return <ArrowUpRight className="w-4 h-4" />;
      case 'yield': return <TrendingUp className="w-4 h-4" />;
      case 'decision': return <Zap className="w-4 h-4" />;
      case 'alert': return <Shield className="w-4 h-4" />;
      case 'ai_decision': return <Cpu className="w-4 h-4" />;
      case 'market_intelligence': return <Globe className="w-4 h-4" />;
      case 'swap_deposit': return <RefreshCw className="w-4 h-4" />;
      case 'swap_fulfilled': return <CheckCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'transaction': return 'text-primary';
      case 'yield': return 'text-green-400';
      case 'decision': return 'text-secondary';
      case 'alert': return 'text-yellow-400';
      case 'ai_decision': return 'text-blue-400';
      case 'market_intelligence': return 'text-purple-400';
      case 'swap_deposit': return 'text-amber-400';
      case 'swap_fulfilled': return 'text-green-400';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'low': return 'risk-low';
      case 'medium': return 'risk-medium';
      case 'high': return 'risk-high';
      default: return '';
    }
  };

  if (loading || walletLoading) {
    // Show cached data immediately while loading, only show spinner if no cached data
    const hasCachedData = agents.length > 0 || stats || activities.length > 0;
    
    if (!hasCachedData) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
            <span className="font-mono text-muted-foreground">
              {walletLoading ? 'Restoring wallet...' : 'Loading...'}
            </span>
          </div>
        </div>
      );
    }
    // Otherwise continue to render with cached data
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Dashboard Sub-header with PEG status */}
      <div className="glass border-b border-border px-4 py-3">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <Badge variant="outline" className="font-mono text-xs tracking-wider">DASHBOARD</Badge>
          <div className="flex items-center gap-2">
            {pegStatus && (
              <Badge 
                variant="outline" 
                className={`font-mono text-xs ${pegStatus.status === 'stable' ? 'border-green-500/50 text-green-400' : 'border-yellow-500/50 text-yellow-400'}`}
                data-testid="header-peg-status"
              >
                <span className={`w-2 h-2 rounded-full mr-2 ${pegStatus.status === 'stable' ? 'bg-green-500' : 'bg-yellow-500'} pulse-live`}></span>
                PEG: {pegStatus.ratio.toFixed(4)}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-[1800px] mx-auto">
          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Agents List */}
            <div className="lg:col-span-2 space-y-6">
              {/* BOTS Section - Always show */}
              <Card className="glass-card border-secondary/30">
                <CardHeader className="border-b border-border bg-secondary/5 px-3 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="font-heading text-lg tracking-wider flex items-center gap-2">
                        <Zap className="w-5 h-5 text-secondary" />
                        Bots
                        <Badge variant="outline" className="text-secondary border-secondary/50 text-xs">
                          {predictionBots.length} active
                        </Badge>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono"
                          title={liveConnected ? "Real-time WebSocket connected" : "Offline — falling back to polling"}
                          data-testid="dashboard-bots-live-status"
                        >
                          <span className="relative flex h-1.5 w-1.5">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${liveConnected ? "bg-green-400" : "bg-yellow-400"}`} />
                            <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${liveConnected ? "bg-green-400" : "bg-yellow-400"}`} />
                          </span>
                          <span className={liveConnected ? "text-green-300" : "text-yellow-300"}>
                            {liveConnected ? "LIVE" : "POLL"}
                          </span>
                        </span>
                      </CardTitle>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate('/create?type=bot')}
                        className="border-secondary/50 text-secondary hover:bg-secondary/10 px-2"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        New Bot
                      </Button>
                    </div>
                    {predictionBots.length > 0 && (
                      <div className="relative mt-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          value={botSearch}
                          onChange={(e) => setBotSearch(e.target.value)}
                          placeholder="Search bots by name (e.g. R5, H3)"
                          data-testid="bot-search-input"
                          className="w-full bg-background/60 border border-border rounded-md pl-8 pr-8 py-1.5 text-sm outline-none focus:border-secondary/60 transition-colors"
                        />
                        {botSearch && (
                          <button
                            onClick={() => setBotSearch("")}
                            data-testid="bot-search-clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-0">
                    {predictionBots.length === 0 ? (
                      <div className="p-8 text-center">
                        <Zap className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                        <p className="text-muted-foreground text-sm mb-3">No bots deployed yet</p>
                        <Button size="sm" variant="outline" onClick={() => navigate('/create?type=bot')}>
                          <Plus className="w-4 h-4 mr-1" />
                          Create Your First Bot
                        </Button>
                      </div>
                    ) : (
                      <>
                    <ScrollArea className="h-[300px] no-x-scroll" data-testid="bots-scroll-area">
                      <div className="divide-y divide-border">
                        {/* Sort bots by P/L highest to lowest, filtered by search */}
                        {[...predictionBots]
                          .filter((bot) => !botSearch.trim() || (bot.name || "").toLowerCase().includes(botSearch.trim().toLowerCase()))
                          .sort((a, b) => (b.total_pnl_pct || 0) - (a.total_pnl_pct || 0)).map((bot) => {
                          const liveFlash = liveFlashes[bot.id];
                          const liveBump = liveBumps[bot.id];
                          const beating = !!liveHeartbeats[bot.id];
                          // Per-row tick progress 0..1 (constantly moves every second)
                          let tickProg = 0;
                          let tickIn = null;
                          if (bot.status === "running") {
                            const lastTicked = bot.tick_state?.last_ticked_at || bot.last_trade_at;
                            if (lastTicked) {
                              try {
                                const elapsed = (Date.now() - new Date(lastTicked).getTime()) / 1000;
                                const remaining = Math.max(0, 60 - (elapsed % 60));
                                tickIn = Math.ceil(remaining);
                                tickProg = Math.max(0, Math.min(1, 1 - remaining / 60));
                              } catch (_e) {
                                /* ignore */
                              }
                            }
                          }
                          return (
                          <div 
                            key={bot.id}
                            className={`relative p-3 hover:bg-muted/30 transition-all cursor-pointer overflow-hidden ${
                              liveFlash === "up" ? "ring-1 ring-inset ring-green-400/40 bg-green-500/5" :
                              liveFlash === "down" ? "ring-1 ring-inset ring-red-400/40 bg-red-500/5" : ""
                            } ${beating ? "bg-green-500/5" : ""}`}
                            onClick={() => navigate(`/bot/${bot.id}`)}
                            data-testid={`bot-row-${bot.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${
                                  bot.status === 'running' ? 'bg-green-500/10' : 'bg-secondary/10'
                                }`}>
                                  <Zap className={`w-5 h-5 ${
                                    bot.status === 'running' ? 'text-green-400' : 'text-secondary'
                                  }`} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-semibold text-sm truncate">{bot.name ? `Bot ${bot.name}` : 'Bot'}</h3>
                                    <Badge 
                                      variant="outline" 
                                      className={`text-[10px] px-1.5 py-0 ${
                                        bot.status === 'running' 
                                          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                          : bot.status === 'killed'
                                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                            : 'bg-muted text-muted-foreground'
                                      }`}
                                    >
                                      {bot.status === 'running' && (
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse mr-1" />
                                      )}
                                      {bot.status?.toUpperCase()}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {fmtStxoshiWhole((bot.total_balance_stxoshi || 0) / 1_000_000)} {dashUnit}
                                    {tickIn !== null && (
                                      <span className="ml-2 text-green-400/80 font-mono" data-testid={`bot-next-tick-${bot.id}`}>
                                        · next tick {tickIn}s
                                      </span>
                                    )}
                                  </p>
                                  {(bot.opened_at || bot.created_at) && (
                                    <p className="text-[10px] text-muted-foreground/70" data-testid={`bot-opened-${bot.id}`}>
                                      Opened {new Date(bot.opened_at || bot.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0 relative pr-1 min-w-[4.75rem]" data-testid={`bot-pnl-col-${bot.id}`}>
                                <p className={`font-mono font-semibold text-sm tabular-nums whitespace-nowrap transition-colors flex items-center gap-1 justify-end ${
                                  (bot.total_pnl_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                                } ${liveFlash ? 'animate-pulse' : ''}`}>
                                  <AnimatedNumber
                                    value={bot.total_pnl_pct || 0}
                                    decimals={2}
                                    prefix={(bot.total_pnl_pct || 0) >= 0 ? '+' : ''}
                                    suffix="%"
                                    testId={`bot-pnl-${bot.id}`}
                                  />
                                </p>
                                <p className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                                  <AnimatedNumber
                                    value={bot.total_trades || 0}
                                    testId={`bot-trades-${bot.id}`}
                                  />{' '}trades
                                </p>
                                {liveBump ? (
                                  <span className="absolute right-1 -bottom-3.5 text-[10px] text-green-400 font-semibold animate-pulse tabular-nums" data-testid={`bot-live-bump-${bot.id}`}>
                                    +{liveBump}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            {/* Per-row tick progress bar — visibly moves every second
                                so the page never feels static. Pulses bright on heartbeat. */}
                            {bot.status === "running" && (
                              <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-green-500/10" aria-hidden>
                                <div
                                  className={`h-full transition-[width] duration-1000 ease-linear ${
                                    beating
                                      ? "bg-green-300 shadow-[0_0_8px_rgba(74,222,128,0.8)]"
                                      : "bg-gradient-to-r from-green-500/40 to-green-400"
                                  }`}
                                  style={{ width: `${Math.round(tickProg * 100)}%` }}
                                  data-testid={`bot-tick-progress-${bot.id}`}
                                />
                              </div>
                            )}
                          </div>
                          );
                        })}
                        {botSearch.trim() && [...predictionBots].filter((bot) => (bot.name || "").toLowerCase().includes(botSearch.trim().toLowerCase())).length === 0 && (
                          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="bot-search-empty">
                            No bots match "{botSearch}"
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                    {predictionBots.length > 5 && !botSearch.trim() && (
                      <div className="p-3 text-center border-t border-border">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/bots')}>
                          View all {predictionBots.length} bots
                        </Button>
                      </div>
                    )}
                      </>
                    )}
                  </CardContent>
                </Card>

              {/* AGENTS Section */}
              <Card className="glass-card border-primary/30">
                <CardHeader className="border-b border-border bg-primary/5 px-3 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="font-heading text-lg tracking-wider flex items-center gap-2">
                      <Bot className="w-5 h-5 text-primary" />
                      Agents
                      <Badge variant="outline" className="text-primary border-primary/50 text-xs">
                        {agents.length}
                      </Badge>
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/create')}
                      className="border-primary/50 text-primary hover:bg-primary/10 px-2"
                      data-testid="agents-create-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New Agent
                    </Button>
                  </div>
                  {/* Swap button on separate row for mobile */}
                  <div className="mt-2">
                    <SwapSTXOSHIButton className="h-8 text-xs w-full" size="sm" />
                  </div>
                  {agents.length > 0 && (
                    <div className="relative mt-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        value={agentSearch}
                        onChange={(e) => setAgentSearch(e.target.value)}
                        placeholder="Search agents by name (e.g. SX)"
                        data-testid="agent-search-input"
                        className="w-full bg-background/60 border border-border rounded-md pl-8 pr-8 py-1.5 text-sm outline-none focus:border-primary/60 transition-colors"
                      />
                      {agentSearch && (
                        <button
                          onClick={() => setAgentSearch("")}
                          data-testid="agent-search-clear"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {agents.length === 0 ? (
                    <div className="p-12 text-center">
                      <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground mb-4">No agents deployed yet</p>
                      <Button onClick={() => navigate('/create')} data-testid="empty-create-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Launch Your First Agent
                      </Button>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[320px] no-x-scroll" data-testid="agents-scroll-area">
                        <div className="divide-y divide-border">
                          {/* Sort agents by yield highest to lowest, filtered by search */}
                          {[...agents]
                            .filter((agent) => !agentSearch.trim() || (agent.name || "").toLowerCase().includes(agentSearch.trim().toLowerCase()))
                            .sort((a, b) => (b.total_yield || 0) - (a.total_yield || 0)).map((agent) => (
                            <div 
                              key={agent.id}
                              className="p-3 hover:bg-muted/30 transition-colors cursor-pointer"
                              onClick={() => navigate(`/agent/${agent.id}`)}
                              data-testid={`agent-row-${agent.id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${(agent.total_yield || 0) > 0 || (agent.active_positions_count || 0) > 0 ? 'bg-green-500/10' : 'bg-primary/10'}`}>
                                    <Bot className={`w-5 h-5 ${(agent.total_yield || 0) > 0 || (agent.active_positions_count || 0) > 0 ? 'text-green-400' : 'text-primary'}`} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h3 className="font-semibold text-sm truncate">{agent.name}</h3>
                                      <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                                        {agent.trust_score}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground font-mono truncate">
                                      {agent.stx_address?.slice(0, 12)}...
                                    </p>
                                    {(agent.opened_at || agent.created_at) && (
                                      <p className="text-[10px] text-muted-foreground/70" data-testid={`agent-opened-${agent.id}`}>
                                        Opened {new Date(agent.opened_at || agent.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                  {/* Show DeFi yield - prioritize STXOSHI, then other protocols */}
                                  {(agent.total_stxoshi_yield || 0) > 0 ? (
                                    <>
                                      <p className="font-mono font-semibold text-sm text-green-400">
                                        {showUsd ? "" : "+"}{fmtStxoshiWhole(agent.total_stxoshi_yield)}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">
                                        {fmtStxoshiWhole(agent.total_stxoshi_value)} {dashUnit}
                                      </p>
                                    </>
                                  ) : (agent.total_defi_yield || 0) > 0 && agent.dominant_asset ? (
                                    <>
                                      <p className="font-mono font-semibold text-sm text-green-400">
                                        {showUsd ? "" : "+"}{fmtAssetWhole(agent.total_defi_yield, agent.dominant_asset)}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">
                                        {fmtAssetWhole(agent.total_defi_value, agent.dominant_asset)} {showUsd && usdRate(agent.dominant_asset) ? "USD" : agent.dominant_asset}
                                      </p>
                                    </>
                                  ) : (agent.total_yield || agent.total_yield_earned || 0) > 0 ? (
                                    <>
                                      <p className="font-mono font-semibold text-sm text-green-400">
                                        {showUsd ? "" : "+"}{fmtAssetWhole(agent.total_yield || agent.total_yield_earned || 0, "stx")}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">{fmtAssetWhole(agent.stx_balance, "stx")} {showUsd && usdRate("stx") ? "USD" : "STX"}</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="font-mono font-semibold text-sm">{agent.sbtc_balance?.toFixed(4) || '0.0000'}</p>
                                      <p className="text-[10px] text-muted-foreground">{agent.stx_balance?.toFixed(2)} STX</p>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {agentSearch.trim() && [...agents].filter((agent) => (agent.name || "").toLowerCase().includes(agentSearch.trim().toLowerCase())).length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="agent-search-empty">
                              No agents match "{agentSearch}"
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                      {agents.length > 0 && !agentSearch.trim() && (
                        <div className="p-3 text-center border-t border-border">
                          <Button variant="ghost" size="sm" onClick={() => navigate('/explore')}>
                            View all {agents.length} agents
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity Feed */}
            <div>
              <Card className="glass-card h-full">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-xl tracking-wider">RECENT ACTIVITY</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Windowed activity feed (react-window) grouped by day —
                      handles 1000s of entries at smooth 60fps scroll. */}
                  <VirtualActivityFeed
                    activities={activities}
                    height={400}
                    getActivityIcon={getActivityIcon}
                    getActivityColor={getActivityColor}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Stats Row — moved below the activity feed */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            <Card className="glass-card" data-testid="stat-total-tvl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total TVL</span>
                  <Bitcoin className="w-4 h-4 text-primary" />
                </div>
                <p className="font-mono text-2xl font-bold">{stats?.total_tvl_sbtc?.toFixed(6) || '0'} sBTC</p>
                <p className="text-xs text-muted-foreground font-mono">≈ ${stats?.total_tvl_usd?.toLocaleString() || '0'}</p>
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="stat-total-agents">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Active Bots & Agents</span>
                  <Bot className="w-4 h-4 text-secondary" />
                </div>
                <p className="font-mono text-2xl font-bold">{stats?.total_agents || 0}</p>
                <p className="text-xs text-muted-foreground">Running 24/7</p>
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="stat-total-activities">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Activities</span>
                  <Activity className="w-4 h-4 text-accent" />
                </div>
                <p className="font-mono text-2xl font-bold">{stats?.total_activities || 0}</p>
                <p className="text-xs text-muted-foreground">Decisions & Transactions</p>
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="stat-peg-health">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Peg Health</span>
                  <Shield className="w-4 h-4 text-green-400" />
                </div>
                <p className={`font-mono text-2xl font-bold ${pegStatus?.status === 'stable' ? 'text-green-400' : 'text-yellow-400'}`}>
                  {pegStatus?.status?.toUpperCase() || 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground font-mono">TVL: {pegStatus?.tvl?.toLocaleString() || '0'} BTC</p>
              </CardContent>
            </Card>
          </div>

          {/* Yield Strategies */}
          <div className="mt-8">
            <Card className="glass-card">
              <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl tracking-wider">YIELD OPPORTUNITIES</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchData}
                    data-testid="refresh-yields-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* STXOSHI Vault - Featured High Yield */}
                <div className="mb-6">
                  <Card 
                    className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500/50 hover:border-amber-400 transition-colors cursor-pointer"
                    data-testid="stxoshi-vault-featured"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                              🔥 High Yield 25%
                            </span>
                          </div>
                          <h4 className="font-bold text-lg text-amber-100">STXOSHI Vault</h4>
                          <p className="text-xs text-amber-200/70">Native STXOSHI coin staking • Guaranteed 25% APY</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-3xl font-bold text-amber-400">25%</p>
                          <p className="text-xs text-amber-200/70">APY</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Other Yield Strategies */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {yields.map((strategy, index) => (
                    <Card 
                      key={index}
                      className="bg-muted/30 border-border hover:border-primary/30 transition-colors"
                      data-testid={`yield-strategy-${index}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-sm">{strategy.protocol}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${getRiskBadge(strategy.risk_level)}`}>
                            {strategy.risk_level}
                          </span>
                        </div>
                        <p className="font-mono text-2xl font-bold text-primary mb-1">
                          {strategy.apy}%
                        </p>
                        <p className="text-xs text-muted-foreground mb-2">APY</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{strategy.type}</span>
                          <span className="font-mono">{strategy.tvl} sBTC</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Portfolio Chart */}
          <div className="mt-8">
            <Card className="glass-card">
              <CardHeader className="border-b border-border">
                <CardTitle className="font-heading text-xl tracking-wider">PLATFORM TVL (24H)</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF9F43" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#FF9F43" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis 
                        dataKey="time" 
                        stroke="#71717A"
                        tick={{ fill: '#71717A', fontSize: 12, fontFamily: 'JetBrains Mono' }}
                      />
                      <YAxis 
                        stroke="#71717A"
                        tick={{ fill: '#71717A', fontSize: 12, fontFamily: 'JetBrains Mono' }}
                        tickFormatter={(value) => `${value} sBTC`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(10, 10, 10, 0.95)', 
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          fontFamily: 'JetBrains Mono'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#FF9F43" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorValue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
